import { Injectable, inject, signal } from "@angular/core";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import {
  AccountInfo,
  CacheLookupPolicy,
  EventType,
  InteractionStatus,
  RedirectRequest,
  SilentRequest,
} from "@azure/msal-browser";
import { Observable, from } from "rxjs";
import { catchError, filter, map } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private readonly msalService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private initializationPromise: Promise<void> | null = null;
  private redirectInProgress = false;

  // Keep user state as a signal for modern Angular
  private readonly userNameSignal = signal<string | null>(null);
  private readonly tokenExpirationSignal = signal<Date | null>(null);

  // Read-only property to access the userName signal
  get userName(): string | null {
    return this.userNameSignal();
  }

  // Read-only property to access token expiration
  get tokenExpiration(): Date | null {
    return this.tokenExpirationSignal();
  }

  constructor() {}

  private async ensureInitialized(): Promise<void> {
    this.initializationPromise ??= this.initializeMsal();

    await this.initializationPromise;
  }

  private async initializeMsal(): Promise<void> {
    try {
      // Make sure MSAL instance is initialized before using it
      await this.msalService.instance.initialize();
      this.msalBroadcastService.inProgress$
        .pipe(
          filter(
            (status: InteractionStatus) => status === InteractionStatus.None
          )
        )
        .subscribe(() => {
          const activeAccount = this.msalService.instance.getActiveAccount();
          if (!activeAccount) {
            const accounts = this.msalService.instance.getAllAccounts();
            if (accounts.length > 0) {
              this.msalService.instance.setActiveAccount(accounts[0]);
            }
          }
        });

      this.msalService.instance.addEventCallback((event) => {
        this.handleAuthEvent(event);
      });

      const account =
        this.msalService.instance.getActiveAccount() ||
        this.msalService.instance.getAllAccounts()[0] ||
        null;
      if (account) this.setUserName(account);
    } catch (error) {
      console.error("Failed to initialize MSAL:", error);
    }
  }

  private handleAuthEvent(event: any): void {
    if (
      event.eventType === EventType.LOGIN_SUCCESS ||
      event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
    ) {
      this.handleLoginOrTokenSuccess(event);
    }
    if (event.eventType === EventType.LOGOUT_SUCCESS) {
      this.handleLogoutSuccess();
    }
  }

  private handleLoginOrTokenSuccess(event: any): void {
    if (event.payload && "account" in event.payload) {
      const payload = event.payload as { account: AccountInfo };
      const account = payload.account;
      this.setUserName(account);
      this.updateTokenExpirationFromEvent(event);
    }
  }

  private handleLogoutSuccess(): void {
    this.setUserName(null);
    this.tokenExpirationSignal.set(null);
  }

  private updateTokenExpirationFromEvent(event: any): void {
    if (event.payload && "expiresOn" in event.payload) {
      const expiresOn = event.payload.expiresOn;
      let expiration: Date | null = null;
      if (expiresOn != null) {
        expiration =
          expiresOn instanceof Date ? expiresOn : new Date(expiresOn);
      }
      this.tokenExpirationSignal.set(expiration);
    }
  }

  public async login(): Promise<void> {
    try {
      await this.ensureInitialized();
      this.msalService.loginRedirect();
    } catch (error) {
      console.error("Login failed:", error);
    }
  }

  public async logout(): Promise<void> {
    try {
      await this.ensureInitialized();
      this.msalService.logoutRedirect();
      this.setUserName(null);
      this.tokenExpirationSignal.set(null);
    } catch (error) {
      console.error("Logout failed:", error);
      this.setUserName(null); // Still clear the username even if logout fails
      this.tokenExpirationSignal.set(null);
    }
  }

  public async isAuthenticated(): Promise<boolean> {
    try {
      await this.ensureInitialized();
      return this.msalService.instance.getActiveAccount() !== null;
    } catch (error) {
      console.error("Authentication check failed:", error);
      return false;
    }
  }

  /**
   * Acquires an access token for the specified scopes, with automatic refresh handling
   * @param scopes The scopes to request the token for
   * @returns An Observable with the access token
   */
  public getAccessToken(scopes: string[]): Observable<string> {
    return from(this.acquireToken(scopes)).pipe(
      map((response) => {
        // Update token expiration
        if (response.expiresOn) {
          this.tokenExpirationSignal.set(response.expiresOn);
        }
        return response.accessToken;
      }),
      catchError((error) => {
        console.error("Error acquiring token:", error);
        throw error;
      })
    );
  }

  /**
   * Acquire a token from cache or refresh token, redirecting on failure without iframe renewal
   * @param scopes The scopes to request
   * @returns The authentication result containing the token
   */
  private async acquireToken(scopes: string[], forceRefresh = false) {
    await this.ensureInitialized();

    const account = this.msalService.instance.getActiveAccount();

    if (!account) {
      throw new Error(
        "No active account! Please sign in before acquiring tokens."
      );
    }

    const request: SilentRequest = {
      scopes,
      account,
      forceRefresh,
      // These policies fail instead of falling back to a hidden iframe.
      cacheLookupPolicy: forceRefresh
        ? CacheLookupPolicy.RefreshToken
        : CacheLookupPolicy.AccessTokenAndRefreshToken,
    };

    try {
      // Try to get token silently - this will use the token from cache or refresh it if needed
      return await this.msalService.instance.acquireTokenSilent(request);
    } catch (error) {
      if (!this.redirectInProgress) {
        this.redirectInProgress = true;
        console.warn("Token renewal failed; redirecting to sign in.");
        const redirectRequest: RedirectRequest = { scopes, account };
        try {
          await this.msalService.instance.acquireTokenRedirect(redirectRequest);
        } catch (redirectError) {
          this.redirectInProgress = false;
          throw redirectError;
        }
      }
      throw error;
    }
  }

  /**
   * Manually trigger a token refresh
   * @param scopes The scopes to refresh the token for
   * @returns Observable of the refreshed token
   */
  public refreshToken(scopes: string[]): Observable<string> {
    return from(this.acquireToken(scopes, true)).pipe(
      map((response) => {
        if (response.expiresOn) {
          this.tokenExpirationSignal.set(response.expiresOn);
        }
        return response.accessToken;
      })
    );
  }

  private setUserName(account: AccountInfo | null): void {
    const name = account?.name ?? null;
    this.userNameSignal.set(name);
  }
}
